import React, { useState, useRef, useContext } from 'react';
import _ from 'lodash';
import TextFieldSection from '../TextFieldSection';
import { ToolSection } from '../ToolSection';
import NumberFieldSection from '../NumberFieldSection';
import {
  TextCollectionFieldType,
  TextCollectionField,
} from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import { makeStyles } from 'design-system/styles';
import CategoryFieldSection from './legacy/CategoryFieldSection';
import BooleanFieldSection from '../BooleanFieldSection';
import FileUploadFieldSection from '../FileUploadFieldSection';
import { Tooltip } from 'design-system';
import { isBlankField } from '@scale/shared/textcollection/helpers';
import { isInlineField } from '@scale/shared/textcollection/field';
import clsx from 'clsx';
import { WorkspaceSection } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceSection';
import WfeButtonFieldSection from 'react-scale/components/scalers/tasks/textcollection/WfeButtonFieldSection';
import ResponseTemplatingPopper from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplatingPopper';
import { useChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import ScoreSelectionFieldSection from './ScoreSelectionFieldSection';
import DropdownFieldSection from './DropdownFieldSection';
import { MarkdownFieldSection } from 'react-scale/components/scalers/tasks/textcollection/MarkdownFieldSection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import TutorialFieldContainer from 'react-scale/components/scalers/tasks/tutorial/TutorialFieldContainer';
import SimpleTextFieldSection from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/SimpleTextFieldSection';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatMessageDisplayConfig } from '../../ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import InstructionSection from './fields/SimpleInstructionFieldSection';
import RubricScoresFieldSection from './fields/RubricScoresFieldSection';
import { AudioRecordingFieldSection } from 'react-scale/components/scalers/tasks/textcollection/AudioRecording/AudioRecordingFieldSection';
import MultiChannelAudioUploadFieldSection from 'react-scale/components/scalers/tasks/textcollection/MultiChannelAudioUpload/MultiChannelAudioUploadFieldSection';

interface SimpleFieldSectionProps {
  field: TextCollectionField;
  annotation: TextCollectionAnnotation;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  setAnnotation: (annotation: TextCollectionAnnotation) => void;
  readOnly?: boolean;
  assignmentId?: string;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  readOnlyMessage?: string;
  chatMessageDisplayConfig?: ChatMessageDisplayConfig;
  enableRTLText?: boolean;
}

const shouldBeVisible = (
  field: TextCollectionField,
  annotation: TextCollectionAnnotation,
): boolean => !isInlineField(field) || !field.toggle_message || !isBlankField(annotation);

// eslint-disable-next-line complexity
export default function SimpleFieldSection(props: SimpleFieldSectionProps) {
  const styles = useFormSectionStyles();

  const ref = useRef<HTMLDivElement>(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);

  const { childId, metadata } = useChatStepChildIdContext();
  const { previewMode: isChatPreviewMode, projectId } = useContext(ChatTaskContext);

  const [visible, setVisible] = React.useState<boolean>(
    shouldBeVisible(props.field, props.annotation),
  );
  React.useEffect(() => {
    setVisible(shouldBeVisible(props.field, props.annotation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field, props.annotation]);

  const codingFields = Object.values(props.currentAnnotations).filter(
    annotation => annotation.type === TextCollectionFieldType.Code,
  );

  const disableField = props.readOnly;
  const disableTooltipOverride = !tooltipVisible;
  const tooltipIsDisabled = (!disableField && disableTooltipOverride) || isChatPreviewMode;

  const shouldUseSimpleTextFieldSection = useUnleashFeatureFlag(
    'chat-use-simple-text-field',
    {
      projectId,
    },
    false,
  );
  const FieldSectionComponent = (
    <>
      {visible && (
        <>
          {props.field.type === TextCollectionFieldType.Text &&
            props.annotation.type === TextCollectionFieldType.Text && (
              <>
                {shouldUseSimpleTextFieldSection ? (
                  <SimpleTextFieldSection
                    field={props.field}
                    annotation={props.annotation}
                    setAnnotation={props.setAnnotation}
                    disabled={disableField}
                    chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
                  />
                ) : (
                  <TextFieldSection
                    field={props.field}
                    assignmentId={props.assignmentId}
                    annotation={props.annotation}
                    setAnnotation={props.setAnnotation}
                    disabled={disableField}
                    setSingleAnnotation={() => {}}
                    codingFields={codingFields}
                    chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
                    enableRTLText={props.enableRTLText}
                  />
                )}
              </>
            )}
          {props.field.type === TextCollectionFieldType.Select &&
            props.annotation.type === TextCollectionFieldType.Select && (
              <DropdownFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Autocomplete &&
            props.annotation.type === TextCollectionFieldType.Autocomplete && (
              <DropdownFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.WorkspaceContainer &&
            props.annotation.type === TextCollectionFieldType.WorkspaceContainer && (
              <WorkspaceSection
                field={props.field}
                assignmentId={props.assignmentId}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                setSubmitError={props.setSubmitCodeError}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.WfeButton &&
            props.annotation.type === TextCollectionFieldType.WfeButton && (
              <WfeButtonFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Number &&
            props.annotation.type === TextCollectionFieldType.Number &&
            !props.field.use_slider && (
              <NumberFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Number &&
            props.annotation.type === TextCollectionFieldType.Number &&
            props.field.use_slider && (
              <ScoreSelectionFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                currentAnnotations={props.currentAnnotations}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Category &&
            props.annotation.type === TextCollectionFieldType.Category && (
              <CategoryFieldSection
                field={props.field}
                annotation={props.annotation}
                currentAnnotations={props.currentAnnotations}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
          {props.field.type === TextCollectionFieldType.Boolean &&
            props.annotation.type === TextCollectionFieldType.Boolean && (
              <BooleanFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.FileUpload &&
            props.annotation.type === TextCollectionFieldType.FileUpload && (
              <FileUploadFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
          {props.field.type === TextCollectionFieldType.Markdown &&
            props.annotation.type === TextCollectionFieldType.Markdown && (
              <MarkdownFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={!props.readOnly}
                assignmentId={props.assignmentId}
                enableRTLText={props.enableRTLText}
              />
            )}
          {props.chatTaskBehaviorLogMetadata &&
            !_.isNil(props.chatTaskBehaviorLogMetadata.responseIndex) && (
              <TutorialFieldContainer
                fieldId={props.field.field_id}
                stepIndex={props.chatTaskBehaviorLogMetadata.responseIndex}
                response={props.annotation.response?.[0] || ''}
                candidateId={props.chatTaskBehaviorLogMetadata.candidateId}
              />
            )}
          {props.field.type === TextCollectionFieldType.Tool &&
            props.annotation.type === TextCollectionFieldType.Tool && (
              <ToolSection
                field={props.field}
                setAnnotation={props.setAnnotation}
                annotation={props.annotation}
              />
            )}
          {props.field.type === TextCollectionFieldType.Instruction && (
            <InstructionSection
              field={props.field}
              chatMessageDisplayConfig={props.chatMessageDisplayConfig}
            />
          )}
          {props.field.type === TextCollectionFieldType.Rubric &&
            props.annotation.type === TextCollectionFieldType.Rubric && (
              <>
                <RubricScoresFieldSection
                  field={props.field}
                  setAnnotation={props.setAnnotation}
                  annotation={props.annotation}
                  stepIndex={props.chatTaskBehaviorLogMetadata?.responseIndex}
                  candidateId={props.chatTaskBehaviorLogMetadata?.candidateId}
                  disabled={disableField}
                />
              </>
            )}
          {props.field.type === TextCollectionFieldType.AudioRecording &&
            props.annotation.type === TextCollectionFieldType.AudioRecording && (
              <AudioRecordingFieldSection
                annotation={props.annotation}
                disabled={disableField}
                field={props.field}
                setAnnotation={props.setAnnotation}
              />
            )}
          {props.field.type === TextCollectionFieldType.MultiChannelAudioUpload &&
            props.annotation.type === TextCollectionFieldType.MultiChannelAudioUpload && (
              <MultiChannelAudioUploadFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
        </>
      )}
    </>
  );

  return (
    <div className={clsx(styles.fieldItem)} ref={ref}>
      <Tooltip
        disableHoverListener={tooltipIsDisabled}
        disableFocusListener={tooltipIsDisabled}
        disableTouchListener={tooltipIsDisabled}
        placement="top"
        title={
          <div className={styles.tooltip}>
            {props.readOnlyMessage
              ? props.readOnlyMessage
              : 'The answer for this field is already provided.'}
          </div>
        }
        onClose={() => setTooltipVisible(false)}
        onOpen={() => setTooltipVisible(true)}
      >
        {childId ? (
          <div className={styles.row}>
            <div className={styles.grow}>{FieldSectionComponent}</div>
            {childId && (
              <ResponseTemplatingPopper
                childId={childId}
                metadata={{
                  ...(metadata ?? {}),
                  fieldId: props.field.field_id,
                  numberFieldScale:
                    props.field.type === TextCollectionFieldType.Number &&
                    props.field.min !== undefined &&
                    props.field.max !== undefined
                      ? {
                          min: props.field.min,
                          max: props.field.max,
                        }
                      : undefined,
                }}
                options={{ disablePortal: false }}
              />
            )}
          </div>
        ) : (
          <div>{FieldSectionComponent}</div>
        )}
      </Tooltip>
    </div>
  );
}

const useFormSectionStyles = makeStyles(() => {
  return {
    fieldItem: {
      padding: 8,
      display: 'flex',
      flexDirection: 'column',
      whiteSpace: 'pre-line',
    },
    toggleCheckbox: {
      paddingLeft: 0,
    },
    toggleCheckboxContainer: {
      marginBottom: 8,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    tooltip: {
      maxWidth: 496,
    },
    lintIssuesContainer: {
      marginBottom: 8,
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
    },
    grow: {
      flexGrow: 1,
    },
  };
});
